/**
 * What the last two weeks say about this watch's settings.
 *
 * EVERY KNOB THIS FEATURE HAS IS HERE OR NOWHERE. Thresholds in three layers,
 * cooldowns, muted kinds and families, quiet hours, a daily cap on interrupting
 * mail — all real controls, and none of them findable by somebody who has not
 * read the code. This panel is how they are found: it reads the counts and names
 * the one to turn.
 *
 * THE WORDS ARE THE SERVER'S. `jcseo/watch/verdict.py` writes the label, the
 * detail and the fix, and this renders them without rewording, because the tuning
 * chat renders the same struct — two accounts of the same two weeks is the thing
 * that makes a user stop believing either.
 *
 * LOADED ON ITS OWN, after the page. A verdict reads runs, events and
 * observations across every check, and nobody opens a subject page to read a
 * diagnosis — so it must not be something the page waits for.
 */
import { useEffect, useState } from 'react';
import api from '../../services/api';
import { MAX_SHOWN, verdictTone, visible } from './verdictTone';
import type { WatchVerdict } from '../../types';

export default function VerdictPanel({
  subjectId,
  /** Bumped by the parent after a check runs, so the reading is not stale advice. */
  refreshKey = 0,
  /** What to call the panel. The account-wide reading is a different question from one
   *  watch's, so it says so rather than reusing the subject heading. */
  title = 'How these settings are working',
}: {
  /** Omit for the ACCOUNT-WIDE reading — the one that can say which watch is drowning
   *  out the others, which a per-subject gather structurally cannot. */
  subjectId?: number;
  refreshKey?: number;
  title?: string;
}) {
  const [verdict, setVerdict] = useState<WatchVerdict | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    const ask =
      subjectId === undefined
        ? api.getWatchAccountVerdict()
        : api.getWatchVerdict(subjectId);
    ask
      .then((got) => {
        if (alive) setVerdict(got);
      })
      // Silent. A verdict is advice about settings, and a red error where advice
      // would go reads as something being wrong with the watch itself.
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [subjectId, refreshKey]);

  if (!verdict || verdict.signals.length === 0) return null;

  const shown = visible(verdict.signals, expanded ? verdict.signals.length : MAX_SHOWN);

  return (
    <section className="mb-6" data-testid="watch-verdict">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </h2>

      <ul className="space-y-2">
        {shown.signals.map((signal) => {
          const tone = verdictTone(signal.severity);
          return (
            <li key={signal.key} className={`pl-2 ${tone.edge}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span
                  className={`shrink-0 rounded px-1 text-[10px] ${tone.chip}`}
                  title={tone.hint}
                >
                  {tone.label}
                </span>
                <span className="text-xs font-medium text-gray-800 dark:text-gray-100">
                  {signal.label}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-300">
                {signal.detail}
              </p>
              {/* The fix reads differently from the diagnosis on purpose: it is
                  the only part of this panel there is anything to do about. */}
              <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="font-medium">What to do:</span> {signal.fix}
              </p>
            </li>
          );
        })}
      </ul>

      {shown.more > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-[11px] underline text-gray-500 dark:text-gray-400"
        >
          and {shown.more} more
        </button>
      )}
    </section>
  );
}
